/**
 * `Tabs` — локальные вкладки экрана объекта (кабинет хозяйства, карточка
 * контура). Правило дизайн-системы (`docs/design-system/components.md`):
 * локальные вкладки — семантика `tablist`/`tab`/`tabpanel` и стрелки
 * клавиатуры; обязательный шаг работы вкладкой не прячется.
 *
 * Выбранная вкладка живёт в адресе (`?tab=`), а не в памяти компонента:
 * ссылка «карантин контура» из реестра карантина открывает сразу нужную
 * вкладку, «назад» браузера возвращает на прежнюю. Параметр `tab` — локальный
 * фильтр экрана: с переходом на другой экран он не переносится.
 *
 * API:
 *   const [tab, setTab] = useTabParam(['overview', 'water'], 'overview');
 *   <Tabs label="Разделы хозяйства" items={[{ id, label, icon?, badge? }]} value={tab} onChange={setTab} />
 *   <TabPanel id="water" value={tab}>…</TabPanel>
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Icon } from './Icon';
import { Select } from './forms';

const m = defineMessages({
  section: {
    id: 'common.ui.tabs.section',
    defaultMessage: 'Раздел',
    description: 'Подпись выпадающего списка вместо вкладок на узком экране (телефон): выбор раздела карточки; коротко',
  },
});

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  icon?: LucideIcon;
  /** Короткая метка справа: число сигналов, «нет данных». */
  badge?: ReactNode;
  /**
   * Тон метки: `critical` — красный карантин (только критичное состояние),
   * `warning` — есть что разобрать, `quiet` — справочное число.
   */
  tone?: 'critical' | 'warning' | 'quiet';
  /** Пояснение метки для подсказки и чтения с экрана: «70 после срока». */
  badgeLabel?: string;
}

export interface TabsProps<T extends string = string> {
  /** Доступное имя группы вкладок. */
  label: string;
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Префикс id элементов (панели связываются с вкладками по нему). */
  idPrefix?: string;
}

/**
 * Вкладки — строка с подчёркиванием активной (высота 48, отступы 16), значок,
 * подпись и метка-счётчик. Не помещаются по ширине — строка прокручивается
 * (стрелки по краям появляются, только когда есть что прокрутить), активная
 * вкладка сама въезжает в кадр. На узком экране (< 640) вместо строки —
 * выпадающий список разделов (`Select` форм) с теми же метками.
 */
export function Tabs<T extends string>({ label, items, value, onChange, idPrefix = 'tab' }: TabsProps<T>) {
  const t = useT();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const strip = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = strip.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  }, []);

  useEffect(() => {
    measure();
    const el = strip.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, items.length]);

  useEffect(() => {
    const btn = refs.current[value];
    if (btn && typeof btn.scrollIntoView === 'function') btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = items.findIndex((item) => item.id === value);
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;
    event.preventDefault();
    const target = items[next]!;
    onChange(target.id);
    refs.current[target.id]?.focus();
  };
  const scrollBy = (dir: 1 | -1) => strip.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  const badgeText = (item: TabItem<T>) =>
    item.badge === undefined || item.badge === null ? '' : item.badgeLabel ?? (typeof item.badge === 'string' || typeof item.badge === 'number' ? String(item.badge) : '');

  return (
    <div className="atabs">
      <div className="atabs__menu">
        {/* Узкий экран: тот же `Select`, что во всех формах, а не нативный список браузера. */}
        <Select<T>
          label={t(m.section)}
          aria-label={label}
          value={value}
          onChange={(id) => {
            if (id !== '') onChange(id);
          }}
          options={items.map((item) => ({ id: item.id, name: badgeText(item) ? `${item.label} · ${badgeText(item)}` : item.label }))}
        />
      </div>
      <div className="atabs__frame" data-left={edges.left || undefined} data-right={edges.right || undefined}>
        {edges.left ? (
          <button type="button" className="atabs__scroll atabs__scroll--left" tabIndex={-1} aria-hidden="true" onClick={() => scrollBy(-1)}>
            <Icon icon={ChevronLeft} size={16} />
          </button>
        ) : null}
        <div ref={strip} className="atabs__list" role="tablist" aria-label={label} onKeyDown={move} onScroll={measure}>
          {items.map((item) => {
            const selected = item.id === value;
            const hasBadge = item.badge !== undefined && item.badge !== null;
            return (
              <button
                key={item.id}
                ref={(node) => {
                  refs.current[item.id] = node;
                }}
                type="button"
                role="tab"
                id={`${idPrefix}-btn-${item.id}`}
                aria-selected={selected}
                aria-controls={`${idPrefix}-${item.id}`}
                tabIndex={selected ? 0 : -1}
                className="atabs__tab focus-ring"
                title={hasBadge && item.badgeLabel ? `${item.label}: ${item.badgeLabel}` : undefined}
                onClick={() => onChange(item.id)}
              >
                {item.icon ? <Icon icon={item.icon} size={18} /> : null}
                <span className="atabs__label">{item.label}</span>
                {hasBadge ? (
                  <span className={`atabs__badge${item.tone ? ` atabs__badge--${item.tone}` : ''}`}>
                    {item.badge}
                    {item.badgeLabel ? <span className="visually-hidden"> — {item.badgeLabel}</span> : null}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        {edges.right ? (
          <button type="button" className="atabs__scroll atabs__scroll--right" tabIndex={-1} aria-hidden="true" onClick={() => scrollBy(1)}>
            <Icon icon={ChevronRight} size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function TabPanel<T extends string>({
  id,
  value,
  idPrefix = 'tab',
  children,
}: {
  id: T;
  value: T;
  idPrefix?: string;
  children: ReactNode;
}) {
  if (id !== value) return null;
  return (
    <div role="tabpanel" id={`${idPrefix}-${id}`} aria-labelledby={`${idPrefix}-btn-${id}`} className="atabs__panel">
      {children}
    </div>
  );
}

/**
 * Вкладка из адреса: `?tab=<id>`; старый якорь `#<id>` (например `#quarantine`
 * из прежних ссылок) тоже выбирает вкладку. Неизвестное значение — вкладка по
 * умолчанию.
 */
export function useTabParam<T extends string>(ids: readonly T[], fallback: T): [T, (id: T) => void] {
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const raw = params.get('tab') ?? (hash ? hash.slice(1) : null);
  const value = raw && (ids as readonly string[]).includes(raw) ? (raw as T) : fallback;
  const set = useCallback(
    (id: T) => {
      const next = new URLSearchParams(params);
      if (id === fallback) next.delete('tab');
      else next.set('tab', id);
      setParams(next, { replace: true });
    },
    [params, setParams, fallback],
  );
  return [value, set];
}
